import React, { CSSProperties } from "react";

import { css } from "@emotion/react";

import { SerializedStyles } from "@emotion/react";
import { ReactTFC, UnwrapReactComponent } from "../../../utils/fp/missing-ts-parts";
import { SwarovskiLink } from "../ui-elements/SwarovskiLink";
import { ContentfulImage, ContentfulImageProps } from "./ContentfulImage";
import { CSS_MQ } from "../../../styles/breakpoints";
import cn from "classnames";
import { nonEmpty } from "../../../utils/fp/small-fp-utils";

type DivOrLink = { tag: "div" } | { tag: "a"; link: string };

const WrapperComponent: React.FC<
  React.PropsWithChildren<{
    asElement: DivOrLink;
    className?: string;
    serializedStyles: SerializedStyles | Array<SerializedStyles>;
    style?: CSSProperties;
  }>
> = props =>
  props.asElement.tag === "a" ? (
    <SwarovskiLink
      className={props.className}
      attachStyles={props.serializedStyles}
      isInternal={true}
      link={props.asElement.link}
      styled={"no-decoration"}
      scrollToTop
      style={props.style}
    >
      {props.children}
    </SwarovskiLink>
  ) : (
    <div css={props.serializedStyles} className={props.className} style={props.style}>
      {props.children}
    </div>
  );

const aspectRatioStyles = css`
  position: relative;
  display: block;

  aspect-ratio: var(--ar);

  &:not(.all-children) {
    > :first-of-type {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }

  &.all-children {
    > * {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }

  &.disabled-mobiles {
    ${CSS_MQ.atMostSm} {
      aspect-ratio: unset;

      &:not(.all-children) {
        > :first-of-type {
          position: relative;
        }
      }

      &.all-children {
        > * {
          position: relative;
        }
      }
    }
  }
`;

export const AspectRatio: React.FC<
  React.PropsWithChildren<{
    height: number;
    width: number;
    attachStyles?: SerializedStyles;
    disableOnMobiles?: boolean;
    wrapperClassName?: string;
    allChildren?: boolean;
    asElement?: DivOrLink;
  }>
> = ({ height, width, attachStyles, children, asElement, disableOnMobiles, wrapperClassName, allChildren }) => {
  const asElementWithDefault: DivOrLink = asElement || { tag: "div" };

  return (
    <WrapperComponent
      asElement={asElementWithDefault}
      className={cn(disableOnMobiles ? "disabled-mobiles" : undefined, wrapperClassName, {
        "all-children": allChildren,
      })}
      style={{ "--ar": `${width} / ${height}` } as CSSProperties}
      serializedStyles={[aspectRatioStyles, attachStyles].filter(nonEmpty)}
    >
      {children}
    </WrapperComponent>
  );
};

export const SquareAspectRatio: React.FC<
  React.PropsWithChildren<{
    attachStyles?: SerializedStyles;
    disableOnMobiles?: boolean;
    className?: string;
  }>
> = props => (
  <AspectRatio
    height={1}
    width={1}
    attachStyles={props.attachStyles}
    disableOnMobiles={props.disableOnMobiles}
    wrapperClassName={props.className}
  >
    {props.children}
  </AspectRatio>
);

export const ImageAspectRatio: ReactTFC<
  { aspectRatio: UnwrapReactComponent<typeof AspectRatio>; wrapperClassName?: string } & ContentfulImageProps
> = props => {
  const { aspectRatio, wrapperClassName, ...imageProps } = props;

  return (
    <AspectRatio {...aspectRatio} wrapperClassName={wrapperClassName}>
      <ContentfulImage
        {...imageProps}
        alt={imageProps.alt}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        `}
      />
    </AspectRatio>
  );
};
